import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";

function fmtPct(n, digits = 2) {
  if (n == null) return "—";
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}%`;
}

function fmtDurationDays(d) {
  if (d == null) return "—";
  if (d < 1) return `${Math.max(1, Math.round(d * 24))}h`;
  if (d < 30) return `${d.toFixed(1)}d`;
  const months = d / 30;
  if (months < 12) return `${months.toFixed(1)}mo`;
  return `${(d / 365).toFixed(1)}y`;
}

function Card({ label, value, tone }) {
  const cls = tone === "pos" ? "text-emerald-500" : tone === "neg" ? "text-rose-500" : "text-zinc-100";
  return (
    <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

export default function GlobalStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/global").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="p-8 text-zinc-500 text-sm">Loading…</div>;

  const winData = [
    { name: "Wins", value: stats.wins || 0 },
    { name: "Losses", value: stats.losses || 0 },
  ];
  const COLORS = ["#10B981", "#F43F5E"];

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="page-title">Global Stats</h1>
        <p className="text-sm text-zinc-500">Percentage-based performance across the shared journal.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
        <Card label="Cumulative %" value={fmtPct(stats.cumulative_pct)} tone={stats.cumulative_pct >= 0 ? "pos" : "neg"} />
        <Card label="Avg P&L %" value={fmtPct(stats.avg_pnl_pct)} tone={stats.avg_pnl_pct >= 0 ? "pos" : "neg"} />
        <Card label="Win Rate" value={`${(stats.win_rate || 0).toFixed(1)}%`} />
        <Card label="Avg Win %" value={fmtPct(stats.avg_win_pct)} tone="pos" />
        <Card label="Avg Loss %" value={fmtPct(stats.avg_loss_pct)} tone="neg" />
        <Card label="Profit Factor" value={stats.profit_factor_pct != null ? stats.profit_factor_pct.toFixed(2) : "—"} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card label="Best Trade %" value={fmtPct(stats.best_trade_pct)} tone={stats.best_trade_pct >= 0 ? "pos" : "neg"} />
        <Card label="Worst Trade %" value={fmtPct(stats.worst_trade_pct)} tone={stats.worst_trade_pct >= 0 ? "pos" : "neg"} />
        <Card label="Avg Duration" value={fmtDurationDays(stats.avg_trade_duration_days)} />
        <Card label="Trades w/ Activity" value={stats.trades_with_activity ?? 0} />
        <Card label="Open Positions" value={stats.open_positions_count ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-4">Cumulative Return %</h3>
          <div className="h-72">
            {stats.equity_curve?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.equity_curve}>
                  <defs>
                    <linearGradient id="gs-eq" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ""} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #27272a', fontSize: 12 }} formatter={(v) => [`${v}%`, "Cumulative"]} />
                  <Area type="monotone" dataKey="equity" stroke="#10B981" fill="url(#gs-eq)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No closed trades yet</div>
            )}
          </div>
        </div>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-4">Win / Loss</h3>
          <div className="h-56">
            {(stats.wins + stats.losses) > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={winData} innerRadius={45} outerRadius={75} dataKey="value" stroke="none">
                    {winData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #27272a', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No data</div>
            )}
          </div>
          <div className="flex justify-around mt-2 text-xs font-mono">
            <div className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> {stats.wins} Wins</div>
            <div className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-500 rounded-sm" /> {stats.losses} Losses</div>
          </div>
        </div>
      </div>
    </div>
  );
}
